/**
 * Ссылка-скачка с лоадером (разметка 25.09.26 15:55 п.1): пока файл едет, в
 * правом верхнем углу крутится значок — видно, что клик сработал. `href` у
 * ссылки остаётся настоящим: «открыть в новой вкладке» и средняя кнопка работают
 * по-старому, `fetch` перехватывает только обычный клик. Ошибка — красная рамка
 * и слова сервера во всплывающей подсказке.
 *
 * Клики и клавиши гасятся всегда: ссылка стоит в заголовке группы и в строке
 * партии, и под ней ничего сворачиваться не должно.
 */

import { Loader2 } from "lucide-react";
import { useState, type ReactNode } from "react";

import { downloadFile } from "@/lib/download";

export function DownloadLink({
  href,
  title,
  className,
  children,
  ...rest
}: {
  href: string;
  title?: string | undefined;
  className?: string | undefined;
  children: ReactNode;
  "aria-label"?: string;
  "data-annot"?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const start = async () => {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      await downloadFile(href);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <a
      {...rest}
      href={href}
      download
      title={failure ? `не скачалось: ${failure}` : busy ? "скачиваю…" : title}
      aria-busy={busy}
      onClick={(event) => {
        event.stopPropagation();
        // Модификаторы — вкладка или окно браузера, а не наша скачка.
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        void start();
      }}
      onKeyDown={(event) => event.stopPropagation()}
      className={
        "relative " +
        (className ?? "") +
        (failure ? " rounded ring-1 ring-err" : "") +
        (busy ? " cursor-progress opacity-70" : "")
      }
    >
      {children}
      {busy ? (
        <Loader2
          aria-hidden
          className="pointer-events-none absolute -right-1.5 -top-1.5 h-3 w-3 animate-spin rounded-full bg-bg text-accent-text"
        />
      ) : null}
    </a>
  );
}
